// Visual pest identifier — real photo cards tenants can tap to tell the AI
// exactly what they're dealing with. Photos come from Wikimedia Commons via
// Special:FilePath (redirects to the CDN, no hash path to break). If any URL
// 404s or the tenant is offline, the card falls back to a stylised SVG
// silhouette so the picker is always usable.

import { useState } from "react";
import { ArrowLeft, Warning } from "@phosphor-icons/react";

const WM = (name, width = 320) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(name)}?width=${width}`;

const PESTS = [
  {
    key: "cockroach", name: "Cockroach",
    hint: "Flat body, half-inch long, brown or black, long antennae, runs fast when the light comes on.",
    photo: WM("American-cockroach.jpg"),
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="60" cy="55" rx="30" ry="18" fill="#78350f"/>
        <ellipse cx="60" cy="52" rx="26" ry="15" fill="#92400e"/>
        <path d="M50 40 L38 25 M70 40 L82 25" stroke="#0f172a" strokeWidth="2" strokeLinecap="round"/>
        <path d="M42 55 L28 55 M42 60 L28 68 M42 65 L28 78 M78 55 L92 55 M78 60 L92 68 M78 65 L92 78" stroke="#0f172a" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: "bed_bug", name: "Bed Bug",
    hint: "Size of an apple seed, reddish-brown, oval flat body. Bites in a straight line on your arms, back, or legs.",
    photo: WM("Bed_bug,_Cimex_lectularius.jpg"),
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="60" cy="55" rx="24" ry="18" fill="#7f1d1d"/>
        <ellipse cx="60" cy="52" rx="20" ry="14" fill="#991b1b"/>
        <path d="M46 46 L36 40 M74 46 L84 40" stroke="#0f172a" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: "mouse", name: "Mouse",
    hint: "Small (2–4 inches), grey or brown, big round ears, long thin tail. Rice-sized droppings.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/oluhlrmw_Mouse_white_background.jpg",
    svg: (
      <svg viewBox="0 0 140 100" width="90" height="70" aria-hidden="true">
        <ellipse cx="65" cy="60" rx="28" ry="18" fill="#78716c"/>
        <circle cx="35" cy="55" r="14" fill="#78716c"/>
        <circle cx="28" cy="45" r="6" fill="#57534e"/>
        <path d="M90 60 Q110 55 130 70" stroke="#78716c" strokeWidth="3" fill="none" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: "rat", name: "Rat",
    hint: "Bigger than a mouse (7+ inches), thick body, thick scaly tail. Pill-sized droppings.",
    photo: WM("Rattus_norvegicus_1.jpg"),
    svg: (
      <svg viewBox="0 0 160 100" width="90" height="70" aria-hidden="true">
        <ellipse cx="75" cy="60" rx="38" ry="22" fill="#57534e"/>
        <circle cx="35" cy="55" r="18" fill="#57534e"/>
        <path d="M112 62 Q140 58 152 75" stroke="#57534e" strokeWidth="4" fill="none" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: "ant", name: "Ant",
    hint: "Tiny (~⅛ inch), black or red, three body sections. Usually in a trail marching to food or water.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/3n2jt5jr_fig-1.png",
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <circle cx="35" cy="50" r="8" fill="#0f172a"/>
        <circle cx="55" cy="50" r="6" fill="#0f172a"/>
        <circle cx="75" cy="50" r="10" fill="#0f172a"/>
      </svg>
    ),
  },
  {
    key: "termite", name: "Termite",
    hint: "Looks like a white or cream-colored ant. Straight antennae. Often near wood damage or mud tubes on walls.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/och7l69q_Coptotermes_formosanus_shiraki_USGov_k8204-7.webp",
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="35" cy="50" rx="10" ry="8" fill="#fef3c7" stroke="#a16207" strokeWidth="1.5"/>
        <ellipse cx="60" cy="50" rx="18" ry="9" fill="#fef3c7" stroke="#a16207" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    key: "silverfish", name: "Silverfish",
    hint: "Silvery-grey, teardrop-shaped, three tail bristles. Wiggling, fish-like motion. Loves damp bathrooms.",
    photo: WM("Silverfish.jpg"),
    svg: (
      <svg viewBox="0 0 140 80" width="90" height="60" aria-hidden="true">
        <path d="M20 40 Q60 25 100 40 Q90 45 100 50 Q60 65 20 50 Z" fill="#cbd5e1" stroke="#64748b" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    key: "spider", name: "Spider",
    hint: "Eight legs (not six!), no antennae, round or oval body. Often builds webs in corners.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/wh22wz3f_Tegenaria_domestica-f.jpg",
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="60" cy="55" rx="12" ry="10" fill="#1e293b"/>
        <circle cx="60" cy="42" r="5" fill="#1e293b"/>
        <path d="M50 50 L28 32 M50 55 L22 50 M50 60 L28 72 M52 65 L32 82 M70 50 L92 32 M70 55 L98 50 M70 60 L92 72 M68 65 L88 82" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" fill="none"/>
      </svg>
    ),
  },
  {
    key: "fly", name: "Fly / Fruit Fly",
    hint: "Buzzing, wings. Fruit flies are tiny (⅛ in) around fruit; house flies are bigger and grey.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/wnfspvj8_images.webp",
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="60" cy="55" rx="14" ry="10" fill="#374151"/>
        <ellipse cx="42" cy="42" rx="14" ry="8" fill="#e5e7eb" opacity="0.7"/>
        <ellipse cx="78" cy="42" rx="14" ry="8" fill="#e5e7eb" opacity="0.7"/>
      </svg>
    ),
  },
  {
    key: "wasp_bee", name: "Wasp / Hornet / Bee",
    hint: "Yellow-and-black stripes, wings, stinger. Papery nest under a soffit — call, don't touch.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/8hubkkh3_images-1.jpeg",
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <ellipse cx="60" cy="55" rx="20" ry="12" fill="#fbbf24"/>
        <rect x="50" y="43" width="4" height="24" fill="#0f172a"/>
        <rect x="58" y="43" width="4" height="24" fill="#0f172a"/>
        <rect x="66" y="43" width="4" height="24" fill="#0f172a"/>
      </svg>
    ),
  },
  {
    key: "flea", name: "Flea",
    hint: "Tiny (~1/16 in), dark brown, jumps. Bites on ankles or a pet scratching constantly.",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/7w61lvle_images.jpeg",
    // fallback filenames also tried by Special:FilePath redirect chain:
    // Cat_flea_(Ctenocephalides_felis).jpg
    svg: (
      <svg viewBox="0 0 100 100" width="70" height="70" aria-hidden="true">
        <ellipse cx="50" cy="55" rx="14" ry="18" fill="#78350f" transform="rotate(-15 50 55)"/>
      </svg>
    ),
  },
  {
    key: "unsure", name: "Not Sure",
    hint: "No worries — I'll ask a few short questions. You can also upload a photo and I'll try to identify it.",
    photo: null,
    svg: (
      <svg viewBox="0 0 120 100" width="80" height="70" aria-hidden="true">
        <circle cx="60" cy="50" r="28" fill="#e0e7ff" stroke="#4338ca" strokeWidth="2"/>
        <text x="60" y="65" textAnchor="middle" fontSize="42" fontWeight="900" fill="#4338ca" fontFamily="Georgia">?</text>
      </svg>
    ),
  },
];

function PestPhoto({ pest }) {
  const [failed, setFailed] = useState(!pest.photo);
  return (
    <div className="h-24 w-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
      {!failed ? (
        <img src={pest.photo} alt={pest.name}
          loading="lazy" referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"/>
      ) : (
        <div className="flex items-center justify-center h-full">{pest.svg}</div>
      )}
    </div>
  );
}

export default function PestPicker({ onPick, onBack, disabled = false }) {
  const seed = (pest) => {
    if (pest.key === "unsure") {
      return "I have a pest problem but I'm not sure what kind — can you help me identify it?";
    }
    return `I saw a ${pest.name.toLowerCase()} in my unit — matches the picture I tapped. What should I do?`;
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="pest-picker">
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
          <button onClick={onBack} data-testid="pest-picker-back"
            className="h-9 w-9 border border-white/30 flex items-center justify-center hover:bg-white/10">
            <ArrowLeft weight="bold" size={16}/>
          </button>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Step 1 · Identify</div>
            <div className="font-display font-black text-lg leading-tight">Tap the pest that looks closest</div>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto p-3 space-y-3">
        <div className="p-3 bg-amber-50 border-2 border-amber-500 flex items-start gap-2">
          <Warning weight="fill" size={18} className="text-amber-700 flex-shrink-0 mt-0.5"/>
          <p className="text-xs text-slate-800 leading-relaxed">
            <b>Not sure?</b> Look at the size and body shape. If it doesn't match anything tap
            <span className="mx-1 px-1 bg-indigo-100 text-indigo-800 font-black">Not Sure</span>
            and I'll ask a couple of questions or you can upload a photo.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2" data-testid="pest-grid">
          {PESTS.map(p => (
            <button key={p.key} onClick={() => onPick(p.key, p.name, seed(p))} disabled={disabled}
              data-testid={`pest-card-${p.key}`}
              className="bg-white border-2 border-slate-300 hover:border-amber-600 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed text-left transition-colors overflow-hidden">
              <PestPhoto pest={p}/>
              <div className="p-2">
                <div className="font-display font-black text-sm uppercase tracking-tight leading-tight">{p.name}</div>
                <div className="text-[10px] text-slate-600 leading-tight line-clamp-3 mt-1">{p.hint}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="text-center pt-2">
          <button onClick={onBack} data-testid="pest-picker-skip"
            className="text-xs text-slate-600 underline hover:text-[#0f172a]">
            ← Pick a different issue
          </button>
        </div>
      </div>
    </div>
  );
}
